/**
 * Password Change Form Component
 * @implements FS-001.2.2 - Password Authentication (Backup)
 * @implements US-008b - Password Setup UI (Frontend)
 *
 * Form for users with an existing password to change it.
 * Requires current password verification before setting new password.
 */

import { useState, FormEvent, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { useChangePasswordMutation } from '@/generated/graphql';
import {
  validatePassword,
  passwordsMatch,
  PASSWORD_REQUIREMENTS,
} from '@/utils';

/**
 * Props for PasswordChangeForm
 */
interface PasswordChangeFormProps {
  /** Callback when password is successfully changed */
  onSuccess?: () => void;
}

/**
 * PasswordChangeForm - Form for changing existing password
 *
 * Shows when user.hasPassword === true
 *
 * @implements FS-001.2.2 - Password Authentication (Backup)
 */
export const PasswordChangeForm = ({ onSuccess }: PasswordChangeFormProps) => {
  // Form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Mutation
  const [changePassword, { loading }] = useChangePasswordMutation();

  // Validation
  const validation = useMemo(
    () => validatePassword(newPassword),
    [newPassword],
  );
  const passwordsDoMatch = useMemo(
    () => passwordsMatch(newPassword, confirmPassword),
    [newPassword, confirmPassword],
  );

  // Check if form can be submitted
  const canSubmit =
    currentPassword.length > 0 &&
    validation.isValid &&
    passwordsDoMatch &&
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    !loading;

  /**
   * Handle form submission
   * @implements FS-001.2.2 - Password Authentication (Backup)
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!canSubmit) return;

    try {
      const result = await changePassword({
        variables: {
          input: {
            currentPassword,
            newPassword,
          },
        },
      });

      if (result.data?.changePassword) {
        setSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        onSuccess?.();
      }
    } catch (err) {
      // Handle specific error for wrong current password
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to change password';
      if (
        errorMessage.includes('Current password is incorrect') ||
        errorMessage.includes('UNAUTHORIZED')
      ) {
        setError('Current password is incorrect');
      } else {
        setError(errorMessage);
      }
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        Change Password
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Update your backup password. You will need to enter your current
        password for verification.
      </Typography>

      {/* Success Message */}
      <Collapse in={success}>
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(false)}
        >
          Password changed successfully!
        </Alert>
      </Collapse>

      {/* Error Message */}
      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      </Collapse>

      {/* Current Password Field */}
      <TextField
        label="Current Password"
        type="password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        fullWidth
        required
        disabled={loading}
        autoComplete="current-password"
        sx={{ mb: 2 }}
      />

      {/* New Password Field */}
      <TextField
        label="New Password"
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        fullWidth
        required
        disabled={loading}
        autoComplete="new-password"
        sx={{ mb: 2 }}
      />

      {/* Confirm Password Field */}
      <TextField
        label="Confirm New Password"
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        fullWidth
        required
        disabled={loading}
        autoComplete="new-password"
        error={confirmPassword.length > 0 && !passwordsDoMatch}
        helperText={
          confirmPassword.length > 0 && !passwordsDoMatch
            ? 'Passwords do not match'
            : ''
        }
        sx={{ mb: 3 }}
      />

      {/* Password Requirements */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        New Password Requirements:
      </Typography>
      <List dense disablePadding sx={{ mb: 3 }}>
        {PASSWORD_REQUIREMENTS.map((req) => {
          const isMet = req.test(newPassword);
          return (
            <ListItem key={req.code} disablePadding sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {isMet ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={req.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: isMet ? 'text.primary' : 'text.secondary',
                }}
              />
            </ListItem>
          );
        })}
      </List>

      {/* Submit Button */}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={!canSubmit}
        sx={{ textTransform: 'none' }}
      >
        {loading ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            Changing Password...
          </>
        ) : (
          'Change Password'
        )}
      </Button>
    </Box>
  );
};
